import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SendWhatsApp from './SendWhatsApp';
import EOIForm from './EOIForm';
import EOILinkShare from './EOILinkShare';

// Big, simple actions at the top of a client page for the site team:
// Upload cheque · Upload KYC · Meeting photo — one guided step each, with a
// photo preview, few fields and a clear "Saved" confirmation. Uses the same
// private storage as Documents & KYC (/api/client-docs).
const PHOTO_TYPES = { meeting: 'meeting_photo', broker: 'cp_client_photo' };
const KYC_TYPES = [['pan', 'PAN card'], ['aadhaar', 'Aadhaar'], ['passport', 'Passport'], ['address_proof', 'Address proof'], ['photo', 'Passport photo']];
const inr = (v) => (v ? `₹${Number(v).toLocaleString('en-IN')}` : '');
const today = () => new Date().toISOString().slice(0, 10);

// Shrink phone photos before upload (site data is slow); PDFs pass through.
function shrink(file, maxSide = 1800) {
  return new Promise((resolve) => {
    if (!file || !/^image\/(jpeg|png|webp)$/i.test(file.type) || file.size < 600 * 1024) return resolve(file);
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const k = Math.min(1, maxSide / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => { URL.revokeObjectURL(url); resolve(b ? new File([b], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file); }, 'image/jpeg', 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

const S = {
  bar: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '16px' },
  big: (c) => ({ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 18px', borderRadius: '14px', border: `2px solid ${c}`, background: '#fff', cursor: 'pointer', textAlign: 'left', fontSize: '16px', fontWeight: 700, color: '#111827', minHeight: '64px' }),
  icon: { fontSize: '28px' },
  sub: { display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginTop: '2px' },
  back: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 12px' },
  modal: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', padding: '20px' },
  title: { fontSize: '20px', fontWeight: 800, margin: 0 },
  step: { fontSize: '13px', fontWeight: 700, color: '#2563eb', margin: '16px 0 8px', textTransform: 'uppercase', letterSpacing: '.3px' },
  pick: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '28px 12px', border: '2px dashed #93c5fd', borderRadius: '14px', background: '#eff6ff', cursor: 'pointer', fontSize: '16px', fontWeight: 700, color: '#1d4ed8', width: '100%' },
  lbl: { display: 'block', fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '6px' },
  in: { width: '100%', padding: '12px 14px', border: '1.5px solid #d1d5db', borderRadius: '10px', fontSize: '17px', boxSizing: 'border-box' },
  save: { width: '100%', padding: '15px', borderRadius: '12px', border: 'none', background: '#16a34a', color: '#fff', fontSize: '18px', fontWeight: 800, cursor: 'pointer', marginTop: '18px' },
  cancel: { width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid #d1d5db', background: '#fff', fontSize: '15px', cursor: 'pointer', marginTop: '8px' },
  err: { background: '#fef2f2', color: '#b91c1c', padding: '12px 14px', borderRadius: '10px', fontSize: '15px', marginTop: '12px' },
  chip: (on) => ({ padding: '10px 14px', borderRadius: '999px', border: `1.5px solid ${on ? '#2563eb' : '#d1d5db'}`, background: on ? '#eff6ff' : '#fff', color: on ? '#1d4ed8' : '#374151', fontSize: '15px', fontWeight: 600, cursor: 'pointer' }),
};

const Thumb = ({ apiBase, docId, onOpen }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    axios.get(`${apiBase}/client-docs/file/${docId}`, { responseType: 'blob' })
      .then((r) => { if (/^image\//.test(r.data.type)) { url = URL.createObjectURL(r.data); setSrc(url); } }).catch(() => {});
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [apiBase, docId]);
  return src
    ? <img src={src} alt="" onClick={() => onOpen(src)} style={{ width: '100%', height: '90px', objectFit: 'cover', borderRadius: '8px', cursor: 'zoom-in' }} />
    : <div style={{ width: '100%', height: '90px', borderRadius: '8px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#6b7280' }}>PDF</div>;
};

const ClientQuickActions = ({ apiBase, clientId, clientName }) => {
  const [mode, setMode] = useState(null);   // 'cheque' | 'kyc' | 'meeting'
  const [data, setData] = useState({ documents: [], payments: [], bookings: [] });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [sending, setSending] = useState(false);
  const [eoiOpen, setEoiOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const camRef = useRef(null), galRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${apiBase}/client-docs/${clientId}`).then((r) => setData(r.data)).catch(() => {});
  }, [apiBase, clientId]);
  useEffect(() => { load(); }, [load]);

  const open = (m) => {
    setMode(m); setFile(null); setPreview(null); setErr(''); setDone(null);
    const b = data.bookings || [];
    setF(m === 'cheque' ? { amount: '', cheque_number: '', bank_name: '', cheque_date: today(), sale_id: b.length === 1 ? String(b[0].id) : '' }
      : m === 'kyc' ? { doc_type: 'pan' } : {});
  };
  const close = () => { if (!busy) { setMode(null); if (preview) URL.revokeObjectURL(preview); setPreview(null); } };
  const choose = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw) return;
    const small = await shrink(raw);
    setFile(small); setErr('');
    if (preview) URL.revokeObjectURL(preview);
    setPreview(/^image\//.test(small.type) ? URL.createObjectURL(small) : null);
  };

  const save = async () => {
    if (!file) { setErr(mode === 'cheque' ? 'Take a photo of the cheque first.' : 'Take a photo first.'); return; }
    if (mode === 'cheque' && !(Number(f.amount) > 0)) { setErr('Enter the cheque amount.'); return; }
    setBusy(true); setErr('');
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', mode === 'cheque' ? 'cheque' : PHOTO_TYPES[mode] || f.doc_type);
    ['amount', 'cheque_number', 'bank_name', 'cheque_date', 'sale_id', 'notes'].forEach((k) => { if (f[k]) fd.append(k, f[k]); });
    try {
      await axios.post(`${apiBase}/client-docs/${clientId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone({ preview, text: mode === 'cheque' ? `Cheque of ${inr(f.amount)} saved` : mode === 'meeting' ? 'Meeting photo saved' : mode === 'broker' ? 'Photo with broker saved' : `${(KYC_TYPES.find((t) => t[0] === f.doc_type) || [])[1]} saved` });
      setPreview(null); setFile(null);
      load();
    } catch (e) {
      setErr(e.response?.status === 403 ? 'You are not allowed to upload for clients. Ask your manager.' : (e.response?.data?.error || 'Could not save. Check the internet and try again.'));
    } finally { setBusy(false); }
  };

  const cheques = (data.documents || []).filter((d) => d.doc_type === 'cheque');
  const payFor = (id) => (data.payments || []).find((p) => p.id === id);
  const saleLabel = (id) => { const s = (data.bookings || []).find((b) => b.id === id); return s ? `${s.unit_number}${s.booking_number ? ` · ${s.booking_number}` : ''}` : ''; };
  const totalCheques = cheques.reduce((a, d) => a + (Number(payFor(d.entity_id)?.amount) || 0), 0);
  const titles = { cheque: '📷 Upload cheque', kyc: '🪪 Upload KYC document', meeting: '🤝 Meeting photo', broker: '📸 Photo of the broker with the client' };

  return (
    <div>
      <div style={S.bar}>
        <button style={S.big('#16a34a')} onClick={() => open('cheque')}><span style={S.icon}>📷</span><span>Upload cheque<span style={S.sub}>Photo + amount</span></span></button>
        <button style={S.big('#2563eb')} onClick={() => open('kyc')}><span style={S.icon}>🪪</span><span>Upload KYC<span style={S.sub}>PAN, Aadhaar…</span></span></button>
        <button style={S.big('#7c3aed')} onClick={() => open('meeting')}><span style={S.icon}>🤝</span><span>Meeting photo<span style={S.sub}>Client at the site</span></span></button>
        <button style={S.big('#b45309')} onClick={() => open('broker')}><span style={S.icon}>📸</span><span>Photo with broker<span style={S.sub}>Needed for brokerage</span></span></button>
        <button style={S.big('#ea580c')} onClick={() => setEoiOpen(true)}><span style={S.icon}>📝</span><span>New EOI<span style={S.sub}>Expression of interest</span></span></button>
        <button style={S.big('#0891b2')} onClick={() => setLinkOpen(true)}><span style={S.icon}>🔗</span><span>EOI link<span style={S.sub}>Client fills it on phone</span></span></button>
        <button style={S.big('#25D366')} onClick={() => setSending(true)}><span style={S.icon}>💬</span><span>Send WhatsApp<span style={S.sub}>Thank-you, EOI…</span></span></button>
      </div>

      {cheques.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '14px 16px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
            <b style={{ fontSize: '15px' }}>Cheques received · {cheques.length}</b>
            <span style={{ fontSize: '15px', fontWeight: 700, color: '#16a34a' }}>{inr(totalCheques)}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
            {cheques.map((d) => {
              const p = payFor(d.entity_id);
              return (
                <div key={d.id} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '6px' }}>
                  <Thumb apiBase={apiBase} docId={d.id} onOpen={setZoom} />
                  <div style={{ fontSize: '14px', fontWeight: 700, marginTop: '6px' }}>{inr(p?.amount) || '—'}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {p?.reference_number ? `#${p.reference_number} · ` : ''}{p?.bank_name || ''}
                    {p?.sale_id ? <div>{saleLabel(p.sale_id)}</div> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {mode && (
        <div style={S.back} onClick={close}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={S.title}>{titles[mode]}</h2>
              <button onClick={close} style={{ border: 'none', background: 'none', fontSize: '28px', cursor: 'pointer', color: '#6b7280' }} aria-label="Close">×</button>
            </div>
            <div style={{ fontSize: '14px', color: '#6b7280' }}>For {clientName}</div>

            {done ? (
              <div style={{ textAlign: 'center', padding: '18px 0 4px' }}>
                <div style={{ fontSize: '54px' }}>✅</div>
                <div style={{ fontSize: '20px', fontWeight: 800, color: '#16a34a' }}>{done.text}</div>
                {done.preview && <img src={done.preview} alt="" style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '10px', marginTop: '12px' }} />}
                <button style={S.save} onClick={() => open(mode)}>{mode === 'cheque' ? 'Add another cheque' : 'Add another'}</button>
                <button style={S.cancel} onClick={() => setMode(null)}>Done</button>
              </div>
            ) : (
              <>
                {mode === 'kyc' && (
                  <>
                    <div style={S.step}>1 · Which document?</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {KYC_TYPES.map(([k, l]) => <button key={k} style={S.chip(f.doc_type === k)} onClick={() => setF({ ...f, doc_type: k })}>{l}</button>)}
                    </div>
                  </>
                )}

                <div style={S.step}>{mode === 'kyc' ? '2' : '1'} · {mode === 'cheque' ? 'Photo of the cheque' : 'Photo'}</div>
                <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={choose} style={{ display: 'none' }} />
                <input ref={galRef} type="file" accept="image/*,application/pdf" onChange={choose} style={{ display: 'none' }} />
                {preview || file ? (
                  <div style={{ textAlign: 'center' }}>
                    {preview ? <img src={preview} alt="" style={{ maxWidth: '100%', maxHeight: '260px', borderRadius: '12px', border: '1px solid #e5e7eb' }} />
                      : <div style={{ padding: '18px', background: '#f3f4f6', borderRadius: '12px' }}>📄 {file.name}</div>}
                    <button style={{ ...S.cancel, marginTop: '10px' }} onClick={() => camRef.current?.click()}>Retake photo</button>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <button style={S.pick} onClick={() => camRef.current?.click()}><span style={{ fontSize: '34px' }}>📷</span>Take photo</button>
                    <button style={{ ...S.pick, borderColor: '#d1d5db', background: '#f9fafb', color: '#374151' }} onClick={() => galRef.current?.click()}><span style={{ fontSize: '34px' }}>🖼️</span>From gallery</button>
                  </div>
                )}

                {mode === 'cheque' && (
                  <>
                    <div style={S.step}>2 · Cheque details</div>
                    <label style={S.lbl}>Amount (₹) *</label>
                    <input style={{ ...S.in, fontSize: '22px', fontWeight: 700 }} type="number" inputMode="numeric" min="0" placeholder="e.g. 500000"
                      value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
                    {Number(f.amount) > 0 && <div style={{ fontSize: '14px', color: '#16a34a', fontWeight: 700, marginTop: '4px' }}>{inr(f.amount)}</div>}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '12px' }}>
                      <div><label style={S.lbl}>Cheque no.</label><input style={S.in} inputMode="numeric" value={f.cheque_number} onChange={(e) => setF({ ...f, cheque_number: e.target.value })} /></div>
                      <div><label style={S.lbl}>Date</label><input style={S.in} type="date" value={f.cheque_date} onChange={(e) => setF({ ...f, cheque_date: e.target.value })} /></div>
                    </div>
                    <label style={{ ...S.lbl, marginTop: '12px' }}>Bank</label>
                    <input style={S.in} placeholder="e.g. HDFC Bank" value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} />
                    {(data.bookings || []).length > 0 && (
                      <>
                        <label style={{ ...S.lbl, marginTop: '12px' }}>For flat</label>
                        <select style={S.in} value={f.sale_id} onChange={(e) => setF({ ...f, sale_id: e.target.value })}>
                          <option value="">— not linked —</option>
                          {data.bookings.map((b) => <option key={b.id} value={b.id}>{b.unit_number}{b.booking_number ? ` · ${b.booking_number}` : ''}</option>)}
                        </select>
                      </>
                    )}
                  </>
                )}

                {err && <div style={S.err}>{err}</div>}
                <button style={{ ...S.save, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
                <button style={S.cancel} onClick={close} disabled={busy}>Cancel</button>
                <div style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center', marginTop: '10px' }}>Stored privately — only logged-in staff can see it.</div>
              </>
            )}
          </div>
        </div>
      )}

      {linkOpen && <EOILinkShare apiBase={apiBase} kind="client" clientId={Number(clientId)} clientName={clientName} onClose={() => setLinkOpen(false)} />}
      {eoiOpen && <EOIForm apiBase={apiBase} clientId={Number(clientId)} onClose={(saved) => { setEoiOpen(false); if (saved) load(); }} />}
      {sending && <SendWhatsApp apiBase={apiBase} clients={[{ id: Number(clientId), name: clientName }]} onClose={() => setSending(false)} />}
      {zoom && (
        <div style={S.back} onClick={() => setZoom(null)}>
          <img src={zoom} alt="" style={{ maxWidth: '96vw', maxHeight: '90vh', borderRadius: '10px', background: '#fff' }} />
        </div>
      )}
    </div>
  );
};

export default ClientQuickActions;
